import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  Input,
  ViewChild,
  OnDestroy,
  ElementRef,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef
} from '@angular/core';
import {
  Validators,
  UntypedFormBuilder,
  UntypedFormGroup,
  FormControl,
  ReactiveFormsModule
} from '@angular/forms';
import { adultAndChildrenEmptyValidator } from './adult-and-children-empty-validator';
import { DatePickerComponent } from '../date-picker/date-picker.component';
import { PersonComponent } from '../person/person.component';
import { PromotionCodeValidator } from '../promotion-code/promotion-code.validator';
import { Subscription } from 'rxjs';
import { ShopSearchHelperService } from '../shop-search-helper.service';
import { PoiSearchComponent } from '../poi-search/poi-search.component';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
  BookingParametersInterface,
  BookingParameters,
  copyBookingParameters,
  codetype,
  ActioncodeInterface
} from '../../../entities/bookingParameters';
import { SkihirePoiInterface } from '../../../interfaces/skihire-poi-interface';
import { ShopService, eventTimeType } from '../../../../shared/services/shop.service';
import { PlatformInformationService } from '../../../../../../projects/platform-manager/src/public_api';
import { ResolutionService } from '../../../../core/services/resolution.service';
import { TagManagerService } from '../../../../core/services/tag-manager.service';
import { ScrollService } from '../../../../core/services/scroll.service';
import { ParamsMapperService } from '../../../services/params-mapper.service';
import { DatePipe, NgStyle } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { circleNotch, cross, IconComponent, provideIcons } from '../../../icons';
import { CurrencyService } from '../../../../core/services/currency.service';
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { AlpitranslatePipe } from '../../../../core/pipes/alpitranslate.pipe';
import { CurrencyPipe } from '../../../../core/pipes/currency.pipe';
import { PromotionCodeComponent } from '../promotion-code/promotion-code.component';
import { loaderMap } from './translations/loader-map';
import { CrowdinTranslationService } from '../../../services/crowdin-translation.service';
import { AbTestService } from '../../../abtest';

@Component({
  selector: 'app-shop-search',
  templateUrl: './shop-search.component.html',
  styleUrls: ['./shop-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideIcons({ cross, circleNotch })],
  imports: [
    AlpitranslatePipe,
    CurrencyPipe,
    NgStyle,
    IconComponent,
    PromotionCodeComponent,
    PoiSearchComponent,
    ReactiveFormsModule,
    DatePickerComponent,
    PersonComponent
  ]
})
export class ShopSearchComponent implements OnInit, OnDestroy, AfterViewInit {
  /**
   * the default form settings on initialization
   */
  @Input() bookingParameters: BookingParametersInterface;

  /**
   * set the maximum number of allowed persons for one booking (50 default)
   */
  @Input() maxAllowedPersons = 50;

  /**
   * set the maximum number of allowed children for one booking. This respects the total number of allowed persons (50 default)
   */
  @Input() maxAllowedChildren = 50;

  /**
   * maximum allowed number of days in the picker
   */
  @Input() pickerMaximumRange = 14;

  /**
   * boolean value, if this component is included on the start-page
   */
  @Input() isIncludedOnStart = false;

  @Input() toggle = false;
  public toggleClosed = true;

  @Input() searchButtonAlwaysVisible = false;

  @Input() hidePromocode = false;

  @Input() refererId: string; // sets the referef (partner id) that will be used on the search autocomplete

  /**
   * if true, the sentForm Output will always report searches, even if the form wasn't changed inbetween. It still has to be valid.
   */
  @Input() alwaysReportSearches = false;

  /**
   * form can not be minified. Is allowed by default
   */
  @Input() allowMinification = true;

  /**
   * disable/lock the search input field, user can't click of focus on it anymore
   */
  @Input() disableSearch = false;

  /**
   * analytics on what page this form is implemented on
   */
  @Input() implementationPage: string;

  /**
   * emit an number array with all ages of persons to book
   */
  @Output() changedPersons: EventEmitter<Array<number>> = new EventEmitter<Array<number>>();

  /**
   * emits the new selected location as string
   */
  @Output() changedLocation: EventEmitter<{ slug: string; poi: SkihirePoiInterface }> = new EventEmitter<{
    slug: string;
    poi: SkihirePoiInterface;
  }>();

  @Output() preSetLocation: EventEmitter<{ slug: string; poi: SkihirePoiInterface }> = new EventEmitter<{
    slug: string;
    poi: SkihirePoiInterface;
  }>();

  /**
   * emit the new selected start date
   */
  @Output() changedStartDate: EventEmitter<Date> = new EventEmitter<Date>();

  /**
   * emits the new selected end date
   */
  @Output() changedEndDate: EventEmitter<Date> = new EventEmitter<Date>();

  /**
   * emits the new selected daterange
   */
  @Output() changedDateRange: EventEmitter<{ start: Date; end: Date }> = new EventEmitter<{
    start: Date;
    end: Date;
  }>();

  /**
   * emits on valid promocode changes
   */
  @Output() changedPromocode: EventEmitter<string> = new EventEmitter<string>();

  /**
   * emits all info the search form contains
   */
  @Output() sentForm: EventEmitter<BookingParametersInterface> =
    new EventEmitter<BookingParametersInterface>();
  @Output() skipToDeliveryShop: EventEmitter<{ params: BookingParametersInterface; shopId: string }> =
    new EventEmitter<{ params: BookingParametersInterface; shopId: string }>();

  @Output() updatedUserSession: EventEmitter<BookingParametersInterface> =
    new EventEmitter<BookingParametersInterface>();

  @ViewChild('poiSearch', { static: false }) searchComponent: PoiSearchComponent;
  @ViewChild('datePicker', { static: false }) datePickerComponent: DatePickerComponent;
  @ViewChild('person', { static: false }) personComponent: PersonComponent;
  @ViewChild('searchFormDiv', { static: true }) searchFormDiv: ElementRef;

  _bookingParameters: BookingParameters;
  private cartSub: Subscription;
  private shopSearchHelperSub: Subscription;
  private routerSub: Subscription;
  private formValidationSub: Subscription;
  private minifySub: Subscription;
  protected onSizeReferenceChangeSubscription: Subscription;

  // the search form, used for validation and more
  search_form: UntypedFormGroup;
  delivery = new FormControl(false);

  @Input() deliveryShop: { id: string; price: number };

  // convenience getter for easy access to form fields
  get sf() {
    return this.search_form.controls;
  }
  get personCountHelper() {
    return this.search_form.controls.personGroup.get('personCountHelper');
  }

  get numberAdults() {
    return this._bookingParameters.ages.filter(value => value >= 18).length;
  }

  get numberChildren() {
    return this._bookingParameters.ages.filter(value => value <= 17).length;
  }

  abTestHidePeople: boolean;

  constructor(
    private fb: UntypedFormBuilder,
    private shopService: ShopService,
    protected platformInformation: PlatformInformationService,
    private promotionCodeValidator: PromotionCodeValidator,
    public shopSearchHelper: ShopSearchHelperService,
    public resolutionService: ResolutionService,
    private route: ActivatedRoute,
    private tagManagerService: TagManagerService,
    private scrollService: ScrollService,
    private paramsMapperService: ParamsMapperService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
    public currencySerivce: CurrencyService,
    private analyticsService: AnalyticsService,
    private crowdinTranslationService: CrowdinTranslationService,
    protected abTestService: AbTestService
  ) {
    this.abTestHidePeople =
      !this.platformInformation.getCurrentPartner() &&
      this.platformInformation.getCurrentPlatformShortcode() === 'ar' &&
      this.abTestService.getVersion('noMansSki') === 'v2';
    this.search_form = this.fb.group({
      // location field
      poiGroup: this.fb.group({ poi: ['', Validators.required] }),
      // datum selection
      dateGroup: this.fb.group({
        startDate: ['', Validators.required],
        endDate: ['', Validators.required]
      }),
      // person fields
      personGroup: this.fb.group(
        {
          personCountHelper: [''],
          adults: ['', [Validators.required, Validators.min(0), Validators.pattern(new RegExp('^[0-9]*$'))]],
          numberChildren: [
            '',
            [Validators.required, Validators.min(0), Validators.pattern(new RegExp('^[0-9]*$'))]
          ],
          children: this.fb.array([])
        },
        {
          validators: adultAndChildrenEmptyValidator
        }
      ),
      // promotioncode and service id
      promotionCode: ['', null, [this.promotionCodeValidator]],
      serviceId: ['', [Validators.min(0), Validators.max(999), Validators.pattern(new RegExp('^[0-9]*$'))]]
    });
  }

  ngOnInit() {
    if (this.abTestHidePeople) {
      this.personCountHelper.markAsTouched();
    }
    this.crowdinTranslationService.watchLangChange({
      loaderMap: loaderMap,
      destroyRef: this.destroyRef,
      successCallback: () => this.cdr.detectChanges()
    });
    this.minifySub = this.shopSearchHelper.onFormMinifyChange.subscribe(() => this.cdr.detectChanges());
    this.formValidationSub = this.search_form.statusChanges.subscribe(() => {
      this.cdr.detectChanges();
    });
    this.shopSearchHelperSub = this.shopSearchHelper.newBookingParameters.subscribe(data => {
      this._bookingParameters = data as BookingParameters;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    });

    if (this.platformInformation.getIsBrowser()) {
      this.onSizeReferenceChangeSubscription = this.resolutionService.sizeReferenceChange.subscribe(
        currentSizeReference => {
          if (
            this.shopSearchHelper.formMinified &&
            currentSizeReference > this.resolutionService.mediumReference
          ) {
            this.shopSearchHelper.formMinified = false;
          }
        }
      );
    }
    if (this.bookingParameters === undefined) {
      this._bookingParameters = this.shopService.bookingParameters.copy();
      if (this._bookingParameters.userDefinedSearch === false && !this.abTestHidePeople) {
        this._bookingParameters.ages = [];
      }
    } else {
      this._bookingParameters = copyBookingParameters(this.bookingParameters);
    }

    // "rescue the newsletter" function
    this.routerSub = this.route.queryParamMap.subscribe((params: ParamMap) => {
      const promocode = params.get('promocode');
      if (promocode) {
        this._bookingParameters.codes = [];
        this._bookingParameters.codes.push({ type: codetype.promo, text: promocode });
      } else if (params.get('utm_source') === 'newsletter') {
        if (params.get('promo')) {
          this._bookingParameters.codes = [];
          this._bookingParameters.codes.push({ type: codetype.promo, text: params.get('promo') });
        }
        // 2023: update MT: "We don't patch it for the moment but we probably need it again so keep it in the source code"
        // TODO: [ARPL-434] remove the newsletter quickfix
        // else {
        //   switch (this.platformInformation.getCurrentPlatformShortcode()) {
        //     case 'ar':
        //       this._bookingParameters.codes = [];
        //       this._bookingParameters.codes.push({ type: codetype.promo, text: 'ALPISKI' });
        //       break;
        //     case 'sb':
        //       this._bookingParameters.codes = [];
        //       this._bookingParameters.codes.push({ type: codetype.promo, text: 'SKISTAR19' });
        //       break;
        //     case 'sf':
        //       this._bookingParameters.codes = [];
        //       this._bookingParameters.codes.push({ type: codetype.promo, text: 'SKISTAR19' });
        //       break;
        //     case 'pf':
        //       this._bookingParameters.codes = [];
        //       this._bookingParameters.codes.push({ type: codetype.promo, text: 'SNOW10' });
        //       break;
        //     case 'mc':
        //       this._bookingParameters.codes = [];
        //       this._bookingParameters.codes.push({ type: codetype.promo, text: 'NEIGE2019' });
        //       break;
        //     case 'df':
        //       this._bookingParameters.codes = [];
        //       this._bookingParameters.codes.push({ type: codetype.promo, text: 'SKI2019' });
        //       break;
        //     case 'du':
        //       this._bookingParameters.codes = [];
        //       this._bookingParameters.codes.push({ type: codetype.promo, text: 'SKI2019' });
        //       break;
        //     case 'bp':
        //       this._bookingParameters.codes = [];
        //       this._bookingParameters.codes.push({ type: codetype.promo, text: 'SNOW2019' });
        //       break;
        //   }
        // }
        this.shopSearchHelper.updateBookingParameters(this._bookingParameters);
        this.shopService.cacheBookingParameters(this._bookingParameters);
      }

      if (!this.isIncludedOnStart) {
        try {
          this._bookingParameters = this.paramsMapperService.parseParams(params, this._bookingParameters);
        } catch (e) {
          console.error(e);
        }
        this.shopSearchHelper.updateBookingParameters(this._bookingParameters);
        this.shopService.cacheBookingParameters(this._bookingParameters);
        this.router.navigate([], {
          queryParams: { startDate: null, endDate: null, 'personAges[]': null, promotionCode: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });

    this.shopService.updates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      switch (event.type) {
        // used for interactive mode to signal user he does not need to click "search"
        case eventTimeType.preShopUpdate:
          this.search_form.markAsPristine();
          this.search_form.markAsUntouched();
          break;
        case eventTimeType.preInTown:
          this.shopSearchHelper.loading = true;
          this.shopSearchHelper.loadingOutOfTown = true;
          this.shopSearchHelper.loadingInTown = true;
          this.cdr.markForCheck();
          break;
        case eventTimeType.onInTown:
          this.shopSearchHelper.loadingInTown = false;
          break;
        case eventTimeType.onOutOfTown:
          this.shopSearchHelper.loadingOutOfTown = false;
          this.cdr.markForCheck();
          break;
        case eventTimeType.onAllTown:
          this.search_form.markAsUntouched();
          this.shopSearchHelper.loading = false;
          this.cdr.detectChanges();
          break;
      }
    });

    if (this.isIncludedOnStart === false && this._bookingParameters.userDefinedSearch) {
      this.minifyForm();
    }
    if (this.hidePromocode) {
      this._bookingParameters.setActioncode({ text: '', type: codetype.promo });
    }
    this.search_form.markAsDirty();
  }

  ngOnDestroy() {
    this.minifySub?.unsubscribe();
    this.formValidationSub?.unsubscribe();
    this.cartSub?.unsubscribe();
    this.shopSearchHelperSub?.unsubscribe();
    this.onSizeReferenceChangeSubscription?.unsubscribe();
    this.routerSub?.unsubscribe();
  }

  ngAfterViewInit() {
    if (
      (this.isIncludedOnStart && this.shopSearchHelper.formMinified) ||
      this._bookingParameters.userDefinedSearch === false
    ) {
      this.shopSearchHelper.formMinified = false;
    }
    if (this.isIncludedOnStart) {
      this.search_form.markAsDirty();
    }
  }

  /**
   * user clicked SEARCH, hit ENTER, etc
   */
  public onSubmit() {
    if (this.search_form.valid) {
      // when a user clicks search, it should minify the form as long as it is valid
      this.shopSearchHelper.guidanceMode = false;
      this.shopSearchHelper.customerHasSetDate = true;
      this.minifyForm();
      this.trackBookingParametersEntry();
    }
    if (
      this.search_form.valid &&
      (this.search_form.dirty || this.alwaysReportSearches || this.deliveryShop)
    ) {
      this.search_form.markAsPristine();
      this.search_form.markAsUntouched();
      this._bookingParameters.userDefinedSearch = true;
      if (!this._bookingParameters.slug) {
        // user hasn't selected a poi but clicked search anyway
        this._bookingParameters.slug = this.sf.poiGroup.get('poi').value;
      }

      if (this.deliveryShop) {
        this.analyticsService.activateExperiment('skipToDeliveryShop', this.delivery.value ? 1 : 0);
      }
      if (this.deliveryShop && this.delivery.value) {
        this.skipToDeliveryShop.emit({ params: this._bookingParameters, shopId: this.deliveryShop.id });
      } else {
        this.sentForm.emit(this._bookingParameters);
      }
    } else if (!this.search_form.valid) {
      this.search_form.markAsDirty();
      this.search_form.markAsTouched();
      this.sf.poiGroup.get('poi').markAsTouched();
      this.sf.personGroup.get('personCountHelper').markAsTouched();
      this.sf.personGroup.get('personCountHelper').markAsDirty();
      this.setFocus({ behavior: 'smooth' });
    }
  }

  /**
   * updates the selected skihire poi.
   * @param poi the poi sent in by the eventemitter in poi component
   */
  public selectedPoi(poi: SkihirePoiInterface) {
    this._bookingParameters.poi = poi;
    const parts = poi.url.split('/');
    if (poi.type === 'AlpinTown') {
      this._bookingParameters.slug = parts[3] + '/' + parts[4] + '/' + parts[5];
    } else if (poi.type === 'PointOfInterest') {
      this._bookingParameters.slug = parts[parts.length - 1];
    }
    this.changedLocation.emit({ slug: this._bookingParameters.slug, poi: poi });

    // open calendar for new people
    if (!this.shopSearchHelper.customerHasSetDateTracked && this.shopSearchHelper.guidanceMode) {
      this.datePickerComponent.setFocus();
    }
    this.search_form.markAsDirty();
  }

  setPoi(poi: SkihirePoiInterface) {
    this._bookingParameters.poi = poi;
    const parts = poi.url.split('/');
    if (poi.type === 'AlpinTown') {
      this._bookingParameters.slug = parts[3] + '/' + parts[4] + '/' + parts[5];
      this.preSetLocation.emit({ slug: this._bookingParameters.slug, poi: poi });
    } else if (poi.type === 'AlpinShop') {
      this._bookingParameters.slug = parts[3] + '/' + parts[4] + '/' + parts[5] + '/' + parts[6];
    } else if (poi.type === 'PointOfInterest') {
      this._bookingParameters.slug = parts[parts.length - 1];
    }
  }

  /**
   * updates daterange in form and on session.
   * make sure the daterange is valid!
   * @param dates the dates sent in by the eventemitter in datepicker component
   */
  public _changedDates(dates: { start: Date; end: Date }) {
    this._bookingParameters.start = dates.start;
    this._bookingParameters.end = dates.end;
    this.shopSearchHelper.customerHasSetDate = true;
    if (this.shopSearchHelper.guidanceMode) {
      this.personComponent.setFocus();
    }
    this.trackBookingParametersEntry();
    this.shopSearchHelper.setCustomerHasSetDateTracked();
    this.changedDateRange.emit(dates);
    this.search_form.markAsDirty();
  }

  /**
   * @param start the start date to emit
   */
  public _changedStartDate(start: Date) {
    this.shopSearchHelper.customerHasSetDate = true;
    this._bookingParameters.start = start;
    this.changedStartDate.emit(start);
  }

  /**
   *
   * @param end the end date to emit
   */
  public _changedEndDate(end: Date) {
    this.shopSearchHelper.customerHasSetDate = true;
    this._bookingParameters.end = end;
    this.changedEndDate.emit(end);
  }

  /**
   * updates the persons in form and on user session.
   * make sure the ages are valid!
   * @param ages the new ages of persons to look offers for
   */
  public _updatePersonCount(ages: Array<number>) {
    this._bookingParameters.ages = ages;
    if (this.search_form.valid) {
      this.changedPersons.emit(ages);
      this.search_form.markAsDirty();
    }
  }

  /**
   * updates the promocode in form and on user session.
   * make sure the promocode is valid!
   * @param code valid promocode
   */
  public _changedPromoCode(text: string) {
    const promocode = this._bookingParameters.getPromocode();
    const promotext = promocode ? promocode.text : '';
    if (text.localeCompare(promotext) !== 0) {
      this._bookingParameters.setActioncode({ type: codetype.promo, text: text });
      this.trackBookingParametersEntry();
      this.search_form.markAsDirty();
      this.changedPromocode.emit(text);
    }
  }

  private trackBookingParametersEntry() {
    const periodDate = new Date(this._bookingParameters.start.getTime());
    const dayOfWeek = new DatePipe('en-US').transform(this._bookingParameters.start, 'E');
    let period = new DatePipe('en-US').transform(periodDate, 'yyyy-MM-dd');
    if (dayOfWeek !== 'Sat') {
      // set the date to last Saturday
      periodDate.setDate(periodDate.getDate() - periodDate.getDay() - 1);
      period = new DatePipe('en-US').transform(periodDate, 'yyyy-MM-dd');
    }
    const start = new DatePipe('en-US').transform(this._bookingParameters.start, 'yyyy-MM-dd');
    const end = new DatePipe('en-US').transform(this._bookingParameters.end, 'yyyy-MM-dd');

    this.tagManagerService.push({
      sd: start,
      ed: end,
      sp: period,
      event_name: 'search',
      search_term: this._bookingParameters.poi?.name,
      nr_adults: this._bookingParameters.ages?.filter(a => a >= 36).length,
      nr_children: this._bookingParameters.ages?.filter(a => a < 36).length,
      pc: this._bookingParameters.getPromocode() ? this._bookingParameters.getPromocode().text : ''
    });
  }

  public _changedCouponCode(code: ActioncodeInterface) {
    // remove all coupon codes, add new coupon code
    const couponcode = this._bookingParameters.getCouponcode();
    const couponcodetext = couponcode ? couponcode.text : '';
    if (couponcodetext.localeCompare(code.text) !== 0) {
      this._bookingParameters.setActioncode(code);
      this.shopService.cacheBookingParameters(this._bookingParameters);
    }
  }

  public _changedServiceId(serviceId: string) {
    this._bookingParameters.serviceId = +serviceId;
    this.search_form.markAsDirty();
  }

  /**
   * adds a glowing ring around the submit button if "fireable"
   */
  public submitButtonStyle() {
    if (this.search_form.touched && this.search_form.valid) {
      return {
        position: 'relative',
        'box-shadow': 'rgb(102, 175, 233) 0px 0px 12px 3px',
        border: '1px solid rgb(102, 175, 233)'
      };
    }
    return {};
  }

  minifyForm() {
    if (
      this.resolutionService.getCurrentSizeReference() < this.resolutionService.largeReference &&
      this.allowMinification
    ) {
      this.shopSearchHelper.formMinified = true;
    }
  }

  /**
   * if a new user clicks on a shop with a pristine search form,
   * he'll be guided through the form even if he decides to click
   * on a shop before selection a number of persons
   * @param behavior scroll behavior options
   */
  setFocus(behavior: ScrollIntoViewOptions) {
    this.shopSearchHelper.formMinified = false; // open form if it is closed, just in case
    setTimeout(() => {
      if (this.search_form.get('poiGroup.poi').invalid) {
        this.searchComponent?.setFocus(behavior);
      } else if (this.shopSearchHelper.customerHasSetDate === false) {
        this.datePickerComponent.setFocus(behavior);
      } else {
        this.personComponent?.setFocus(behavior);
      }
    });
  }

  maxifyForm() {
    if (this.shopSearchHelper.formMinified) {
      this.shopSearchHelper.formMinified = false;
      this.scrollService.scrollIntoView(this.searchFormDiv.nativeElement);
    }
  }
}
